import classNames from "classnames";
import { InputProps } from "../BuilderInterface";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates
} from "../helpers";
import "./style.scss";
import { NodeStates, SearchFieldNode } from "../../build/types";
import xIcon from "../../assets/x-icon.svg";
import React from "react";
import DynamicIcon from "../DynamicIcon";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { formatTestId } from "../../utils/format";

const BuilderSearchField = ({ node, value, builderProps, callback, onBlur, onFocus, disabled }: InputProps) => {
    const sNode = node as SearchFieldNode;
    const { id, attr, stateManager, searchIcon, clearIcon, input } = sNode;
    const { setElement: setLabelElement, states: labelStates } = useInteractionStates<HTMLLabelElement>();
    const { setElement: setInputElement, states: inputStates } = useInteractionStates<HTMLInputElement>();
    const { setElement: setClearIconElement, states: clearIconStates } = useInteractionStates<HTMLButtonElement>();

    const searchFieldClass = classNames("builder-search-field", {
        [`${attr?.className}`]: !!attr?.className,
        ["builder-mode"]: !!builderProps
    });

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
        if (callback) {
            const newValue = e.currentTarget.value;
            if (typeof newValue === "string") {
                callback(newValue);
            }
        }
    };

    const handleClearInput = () => {
        if (callback) {
            callback("");
        }
    };

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        const SearchIcon = searchIcon?.src ? (
            <DynamicIcon
                name={searchIcon.src}
                style={getCompoundStyle({
                    otherStyle: searchIcon.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "searchIcon_style")
                })}
                colors={searchIcon.colors}
            />
        ) : null;

        const ClearIcon = sNode.clearIcon?.src ? (
            <DynamicIcon
                name={sNode.clearIcon?.src}
                style={getCompoundStyle({
                    otherStyle: clearIcon?.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "clearIcon_style")
                })}
                colors={sNode.clearIcon?.colors}
            />
        ) : (
            <img src={xIcon} alt="clear" />
        );

        return (
            <label
                {...attr}
                className={searchFieldClass}
                style={getCompoundStyle({
                    node,
                    otherStyle: input?.labelStyle,
                    stateStyle: {
                        ...getStateStyleForBuilder(stateManager, nodeState, "attr_style"),
                        ...getStateStyleForBuilder(stateManager, nodeState, "input_labelStyle")
                    }
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                draggable={onDragStart ? "true" : "false"}
                onDragStart={e => onDragStart?.(e, id)}
                onDragEnd={onDragEnd}
            >
                {input?.label}
                <div className="input-wrapper">
                    {SearchIcon && <div className="search-icon-wrapper">{SearchIcon}</div>}
                    <input
                        type="text"
                        value={value}
                        placeholder={input?.placeholder}
                        readOnly
                        data-testid={formatTestId("builder-search-field", input?.label)}
                        style={getCompoundStyle({
                            otherStyle: input?.style,
                            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "input_style")
                        })}
                    />
                    {value && <button data-testid="builder-search-field-clear-button">{ClearIcon}</button>}
                </div>
            </label>
        );
    } else {
        const labelStyle = getStylesFromInteractiveStates({
            states: labelStates,
            defaultStyle: getCompoundStyle({ node, otherStyle: input?.labelStyle }),
            stateManager,
            paths: ["attr_style", "input_labelStyle"],
            ...(disabled || inputStates.focus
                ? {
                      extraStates: [inputStates.focus && NodeStates.FOCUS, disabled && NodeStates.DISABLED].filter(
                          (state): state is NodeStates => Boolean(state)
                      )
                  }
                : {})
        });

        const inputStyle = getStylesFromInteractiveStates({
            states: inputStates,
            defaultStyle: input?.style,
            stateManager,
            paths: ["input_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        //SearchIcon

        let SearchIcon = null;

        if (searchIcon?.src) {
            const searchStyle = getStylesFromInteractiveStates({
                states: inputStates,
                defaultStyle: searchIcon.style,
                stateManager,
                paths: ["searchIcon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const searchColors = getColorsFromInteractiveStates({
                states: inputStates,
                stateManager,
                paths: ["searchIcon_colors"],
                defaultColors: searchIcon.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            SearchIcon = <DynamicIcon name={searchIcon.src} style={searchStyle} colors={searchColors} />;
        }

        // Clear Icon

        let ClearIcon = <img src={xIcon} alt="clear" />;

        if (clearIcon?.src) {
            const clearStyles = getStylesFromInteractiveStates({
                states: clearIconStates,
                defaultStyle: clearIcon.style,
                stateManager,
                paths: ["clearIcon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const clearColors = getColorsFromInteractiveStates({
                states: clearIconStates,
                stateManager,
                paths: ["clearIcon_colors"],
                defaultColors: clearIcon.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            ClearIcon = <DynamicIcon name={clearIcon.src} style={clearStyles} colors={clearColors} />;
        }

        return (
            <label {...attr} className={searchFieldClass} id={id} ref={setLabelElement} style={labelStyle}>
                {input?.label}
                <div className="input-wrapper">
                    {SearchIcon && <div className="search-icon-wrapper">{SearchIcon}</div>}
                    <input
                        type="text"
                        value={value}
                        placeholder={input?.placeholder}
                        data-testid={formatTestId("builder-search-field", input?.label)}
                        onInput={handleInput}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        ref={setInputElement}
                        style={inputStyle}
                        disabled={disabled}
                    />
                    {value && (
                        <button onClick={handleClearInput} ref={setClearIconElement} data-testid="builder-search-field-clear-button">
                            {ClearIcon}
                        </button>
                    )}
                </div>
            </label>
        );
    }
};

export default BuilderSearchField;
